import { useCallback, useState } from "react";

import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ScrollDismissBoundary } from "app/pages/sales-intelligence/common-components/scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";
import type { AudienceAge } from "app/pages/sales-intelligence/domain/leads/types/enums";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { FilterContentContainer } from "app/pages/sales-intelligence/modules/leads-search-filters/components/filters/styles";
import type FindAudienceAgeFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/AudienceAgeFilter/AudienceAgeFilter";

import { TRANSLATION_PREFIX } from "./const";

const AudienceAgeFilter = ({ filter, value, onFilterUpdate }: IFilterViewBaseProps<FindAudienceAgeFilter>) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedItems = value ?? [];

    const handleSelect = useCallback(
        (selected: AudienceAge) => {
            const values: AudienceAge[] = selectedItems.includes(selected)
                ? selectedItems.filter((v) => v !== selected)
                : [...selectedItems, selected];
            onFilterUpdate(values);
        },
        [selectedItems, onFilterUpdate],
    );

    return (
        <FilterContentContainer>
            <ScrollDismissBoundary
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
            >
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    placement="bottom-start"
                >
                    <Combobox.Trigger>
                        <StyledBaseDropdownButtonWrap>
                            <DropdownButton>{translate(`${TRANSLATION_PREFIX}.button.text`)}</DropdownButton>
                        </StyledBaseDropdownButtonWrap>
                    </Combobox.Trigger>

                    <Combobox.Content>
                        {filter.options.map((id) => (
                            <Combobox.Option
                                key={id}
                                id={id}
                                textValue={translate(`${TRANSLATION_PREFIX}.${id}`)}
                                onClick={() => handleSelect(id)}
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="8px"
                                    width="100%"
                                >
                                    <Combobox.OptionLabel>
                                        {translate(`${TRANSLATION_PREFIX}.${id}`)}
                                    </Combobox.OptionLabel>
                                    <Combobox.OptionCheckbox isChecked={selectedItems.includes(id)} />
                                </Flex>
                            </Combobox.Option>
                        ))}
                    </Combobox.Content>
                </Combobox>
            </ScrollDismissBoundary>
        </FilterContentContainer>
    );
};

export default AudienceAgeFilter;
